import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import {Link, StyledPageCenterContainer, pxToEm} from '../../components/globalStyles';
import HexagonIconImage from '../../components/hexagonIconImage';
import Layout from '../../components/layout/layout';
import BoundModal, {
  StyledBackdropDiv as ModalBackdropDiv,
  StyledContentWrapperDiv as ModalContentWrapperDiv,
  StyledFullscreenDiv as ModalFullscreenDiv
} from '../../components/modal';
import PageViewHeadline from '../../components/pageViewHeadline';
import SeeItInActionButton from '../../components/seeItInActionButton';
import WhoIsUsingSection from '../../components/whoIsUsingSection';
import WrapperAppUl from '../../components/wrapperAppUl';
import {CompanyName} from '../../data/customerData';
import {Product, ProductToHexagonIconSrc, ProductToText} from '../../data/product';
import {AppRoutePathFunction} from '../../logic/routes';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';

import AnalyticsPreviewCarousel, {ImagePreview} from './analyticsPreviewCarousel';
import CloudAnalyticsAccordions from './cloudAnalyticsAccordions';
import NoteworthyFeaturesSection from './noteworthyFeaturesSection';

//#region Styled Components

const StyledFirstColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledSecondColumn = styled(StyledFirstColumn)`
  max-width: 50%;
`;

const StyledTwoColumnsSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  > ${StyledFirstColumn} {
    flex: 1 1 50%;

    :nth-child(1) {
      border-right: 2px solid ${baseVariables.colors.darkBlueGray};
      padding-right: 5%;
    }

    :nth-child(2) {
      padding-left: 5%;
    }
  }
`;

const StyledContentTwoColumnsSection = styled(StyledTwoColumnsSection)`
  padding-top: ${pxToEm(89)}em;
  padding-bottom: ${pxToEm(114)}em;
`;

const StyledListWrapperDiv = styled.div`
  width: 100%;
  padding: 0 ${pxToEm(20)}em;
  margin-bottom: ${pxToEm(45)}em;
`;

const StyledDemoLinkButton = styled(Button)`
  margin-top: ${pxToEm(30)}em;
`;

const StyledH2 = styled.h2`
  font-size: ${pxToEm(24)}em;
  color: ${baseVariables.colors.midnight};
  display: inline-block;
  margin: 0 0 ${pxToEm(55) / pxToEm(24)}em;
`;

const StyledModalImg = styled.img`
  width: 75vw;
  background: white;
  border-radius: ${pxToEm(4)}em;
  padding: ${pxToEm(16)}em;
`;

const StyledP = styled.p`
  font-size: ${pxToEm(20)}em;
  line-height: 1.6;
  margin: 0;
`;

const StyledCloudAnalyticsAccordions = styled(CloudAnalyticsAccordions)`
  margin-top: ${pxToEm(45)}em;
`;
//#endregion

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {}

interface State {
  modalOpened: boolean;
  preview?: ImagePreview;
}

class CloudAnalyticsPageView extends React.Component<Props, State> {
  public state = {
    modalOpened: false,
    preview: undefined
  };

  public componentWillUnmount(): void {
    if (this.state.modalOpened) {
      BoundModal.enableWindowBodyScrolling(true);
    }
  }

  private toggleModal(preview?: ImagePreview) {
    BoundModal.enableWindowBodyScrolling(this.state.modalOpened);

    this.setState({
      modalOpened: !this.state.modalOpened,
      preview
    });
  }

  private renderCarouselImageModal() {
    return (
      <ModalFullscreenDiv>
        <ModalBackdropDiv onClick={() => this.toggleModal()} />
        <ModalContentWrapperDiv>
          <StyledModalImg src={this.state.preview.image} />
        </ModalContentWrapperDiv>
      </ModalFullscreenDiv>
    );
  }

  public render() {
    return (
      <Layout>
        <PageViewHeadline
          topSubHeadline={ProductToText[Product.CloudAnalytics]}
          headline={'Find and fix root causes of streaming issues'}
          badgeComponent={<HexagonIconImage iconSrc={ProductToHexagonIconSrc[Product.CloudAnalytics]} />}
          leftBadgeComponent={
            <SeeItInActionButton
              external={true}
              to="http://bitmovin.com/dashboard/analytics/data?orgId=6af25605-1bd1-458d-8de9-1189f9ccf6df&3rdParty=false&apiKey=5cd462f7-73c1-424e-82d0-1a9bfabcf310" // tslint:disable-line:max-line-length
            />
          }
        />

        {this.state.modalOpened ? this.renderCarouselImageModal() : undefined}
        <StyledPageCenterContainer>
          <StyledContentTwoColumnsSection>
            <StyledFirstColumn>
              <StyledListWrapperDiv>
                <StyledH2>Explore Bitmovin Analytics</StyledH2>
                <StyledP>
                  Bitmovin Analytics is a video analytics tool collecting client-side metrics in four areas:
                </StyledP>
                <WrapperAppUl>
                  <li>Audience Analytics</li>
                  <li>Quality of Service (QoS) Analytics</li>
                  <li>Top Errors</li>
                  <li>Advertising Analytics</li>
                </WrapperAppUl>
                <StyledP>
                  We currently show over 40 metrics with 30 filters and breakdowns across three dashboards. Via the API
                  you get access to around 200 dimensions and filters. <br />
                  All data is fully GDPR compliant.
                </StyledP>
              </StyledListWrapperDiv>
              <NoteworthyFeaturesSection />
            </StyledFirstColumn>

            <StyledSecondColumn>
              <AnalyticsPreviewCarousel onImageClick={(preview?: ImagePreview) => this.toggleModal(preview)} />
              <StyledCloudAnalyticsAccordions />
            </StyledSecondColumn>
          </StyledContentTwoColumnsSection>
        </StyledPageCenterContainer>
        <WhoIsUsingSection
          headline={'Trusted by'}
          companies={[CompanyName.Antel, CompanyName.CBC, CompanyName.ClassPass, CompanyName.TelekomSlovenije]}
        />
      </Layout>
    );
  }

  private renderDemoLinkButton = (demoPath: string) => {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState !== LoadingState.Success) {
      return null;
    }

    const allLoadedDemos = response.demos || [];
    const foundDemo = allLoadedDemos.find(salesDemo => salesDemo.path === demoPath);
    if (foundDemo === undefined) {
      return null;
    }

    return (
      <Link to={AppRoutePathFunction.DemoDetail({demoId: foundDemo.id})}>
        <StyledDemoLinkButton variant={ButtonVariant.OutlinedLight}>{foundDemo.name}&nbsp;Demo</StyledDemoLinkButton>
      </Link>
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(CloudAnalyticsPageView);
